import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import Logger from "../../utils/logger";
import CurrentSession from "../userSession";
import { NOTIFICATION_CONFIGS } from "../../config/notifications";

class MessagingService {
  constructor() {
    this.isNativeMobile = Capacitor.isNativePlatform();
    this.messaging = null;
    this.token = null;
    this.deviceInfo = null;
    this.deviceId = null;
    this.isInitialized = false;

    // Add debounce tracking to prevent duplicate notifications
    this.lastNotificationTime = 0;
    this.lastNotificationType = null;
    this.notificationDebounceMs = 3000; // 3 second debounce

    Logger.debug(
      "Messaging service initialized - Platform:",
      this.isNativeMobile ? "mobile" : "web"
    );
  }

  /**
   * Activate messaging service - request permissions and get FCM token
   * This should only be called after user authentication
   */
  async activateMessaging() {
    if (!this.isNativeMobile) {
      Logger.debug("Web platform - messaging activation not needed");
      return;
    }

    // Check if user is authenticated before requesting permissions
    if (!CurrentSession.isSignedIn) {
      Logger.debug("User not authenticated, skipping messaging activation");
      return;
    }

    try {
      Logger.debug("Activating mobile messaging for authenticated user...");
      await this.initializeMobile();
      this.isInitialized = true;
      Logger.debug("Mobile messaging activated successfully");
    } catch (error) {
      Logger.error("Failed to activate mobile messaging:", error);
      // Don't throw error to prevent app crash
    }
  }

  /**
   * Delete FCM token (for logout) - marks device as inactive
   * @param {string} userUid - User UID to use for Firestore operations
   */
  async deleteToken(userUid = null) {
    try {
      Logger.debug("🔥 MESSAGING: Starting token deletion...");

      // Use provided UID or fall back to current session
      const uid = userUid || CurrentSession.user?.uid;
      Logger.debug("🔥 MESSAGING: User UID:", uid);
      Logger.debug("🔥 MESSAGING: Device info exists:", !!this.deviceInfo);

      if (this.isNativeMobile) {
        await FirebaseMessaging.deleteToken();
        Logger.debug("🔥 MESSAGING: Native token deleted");
      }

      // Mark device as inactive in messaging collection instead of deleting
      if (uid) {
        // Mark device as inactive in messaging collection
        const messagingDocRef = doc(db, "messaging", this.documentId);

        await setDoc(
          messagingDocRef,
          {
            active: false,
            updated: new Date(),
          },
          { merge: true }
        );
        Logger.debug(
          "🔥 MESSAGING: Device marked as inactive in messaging collection"
        );
      } else {
        Logger.debug(
          "🔥 MESSAGING: Cannot mark device as inactive - missing user UID or device info"
        );
      }

      this.token = null;
      Logger.debug("FCM token marked as inactive");
    } catch (error) {
      Logger.error("Failed to delete FCM token:", error);
      // Don't throw error to prevent sign out failure
    }
  }

  get documentId() {
    return `${CurrentSession.authenticatedUser.uid}_${
      this.deviceInfo?.model || "unknown"
    }`;
  }

  /**
   * Generate device-specific document name
   */
  getDeviceDocumentName() {
    if (!this.deviceInfo) {
      return "unknown_device";
    }

    // Create a readable device name combining platform, model, and partial device ID
    const platform = this.deviceInfo.platform || "unknown";
    const model = this.deviceInfo.model || "unknown";
    const deviceIdShort = this.deviceId?.identifier?.slice(-8) || "unknown";

    // Clean up the name to be Firestore-friendly (no special characters)
    const deviceName = `${platform}_${model}_${deviceIdShort}`
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .toLowerCase();

    return deviceName;
  }

  /**
   * Get current FCM token
   */
  getToken() {
    Logger.debug("🔥 MESSAGING: Token value:", this.token);
    return this.token;
  }

  /**
   * Handle incoming messages (when app is in foreground)
   */
  handleMessage(payload) {
    Logger.debug("🔥 MESSAGING: Handling foreground message:", payload);

    // You can customize this based on your notification structure
    const notification = payload.notification || {};
    const data = payload.data || {};

    // For mobile in foreground, we might want to show an in-app notification
    if (this.isNativeMobile) {
      Logger.debug("🔥 MESSAGING: Mobile foreground notification received");
      // Show custom in-app notification or toast
      this.showInAppNotification(notification, data);
    }

    // Show notification if in foreground (web) or handle as needed
    if (!this.isNativeMobile && notification.title) {
      // For web, show a custom notification
      new Notification(notification.title, {
        body: notification.body,
        icon: "/favicon.svg",
        data: data,
      });
    }

    // Handle different message types
    this.handleNotificationByType(data, notification);
  }

  /**
   * Handle notification tap (when app is in background/closed and user taps notification)
   */
  handleNotificationTap(event) {
    Logger.debug("🔥 MESSAGING: Handling notification tap:", event);

    const data = event.notification?.data || {};
    const notification = event.notification || {};

    // Handle different message types
    this.handleNotificationByType(data, notification, true);
  }

  /**
   * Route notification handling based on type
   */
  handleNotificationByType(data, notification, fromTap = false) {
    const action = fromTap ? "tapped" : "received";
    const notificationType = data.type ?? "default";

    Logger.debug(
      `🔥 MESSAGING: Notification ${action} - Type:`,
      notificationType
    );

    // Check if this is a duplicate notification within the debounce window
    const now = Date.now();
    if (
      fromTap &&
      this.lastNotificationType === notificationType &&
      now - this.lastNotificationTime < this.notificationDebounceMs
    ) {
      Logger.debug(
        `🔥 MESSAGING: Skipping duplicate notification of type "${notificationType}" within debounce window`
      );
      return;
    }

    // Update tracking
    this.lastNotificationTime = now;
    this.lastNotificationType = notificationType;

    const config = NOTIFICATION_CONFIGS[notificationType];
    if (!config) {
      Logger.debug("Unknown message type:", notificationType);
      return;
    }

    // Show toast for both foreground and background notifications
    if (typeof window !== "undefined") {
      Logger.debug(
        `🔥 MESSAGING: Dispatching toast for ${notificationType} notification`
      );

      // Build the toast message, appending FCM message if available
      const toastMessage = config.message(data.username, data.message);

      // Debug logging for buddies notifications
      if (notificationType === "buddies") {
        Logger.debug(`🔥 MESSAGING: Buddies notification data:`, data);
        Logger.debug(`🔥 MESSAGING: data.uid:`, data.uid);
        Logger.debug(
          `🔥 MESSAGING: config.autoNavigate:`,
          !!config.autoNavigate
        );
      }

      // Handle auto-navigation for notifications
      if (config.autoNavigate) {
        let navigationUrl;

        // For buddies notifications, we need a uid parameter
        if (notificationType === "buddies" && data.uid) {
          navigationUrl = config.autoNavigate(data.uid);
        }
        // For chat notifications, we need a uid parameter
        else if (notificationType === "chat" && data.uid) {
          navigationUrl = config.autoNavigate(data.uid);
        }
        // For streak notifications, no parameters needed
        else if (notificationType === "streak") {
          navigationUrl = config.autoNavigate();
        }

        if (navigationUrl) {
          Logger.debug(
            `🔥 MESSAGING: Auto-navigating to ${navigationUrl} for ${notificationType} notification`
          );

          // Auto-navigate immediately
          setTimeout(() => {
            if (typeof window !== "undefined" && window.location) {
              Logger.debug(
                `🔥 MESSAGING: Dispatching navigate event to ${navigationUrl}`
              );
              window.dispatchEvent(
                new CustomEvent("navigate", {
                  detail: { url: navigationUrl },
                })
              );
            }
          }, 100); // Small delay to ensure toast shows first
        } else if (notificationType === "buddies") {
          Logger.debug(
            `🔥 MESSAGING: Not auto-navigating buddies - uid: ${data.uid}`
          );
        } else if (notificationType === "chat") {
          Logger.debug(
            `🔥 MESSAGING: Not auto-navigating chat - uid: ${data.uid}`
          );
        }
      }

      // Skip toast if:
      // 1. Config has skipToast flag set to true, OR
      // 2. This is a chat notification opened from tap (direct navigation)
      const shouldSkipToast =
        config.skipToast || (notificationType === "chat" && fromTap);

      if (!shouldSkipToast) {
        window.dispatchEvent(
          new CustomEvent("showToast", {
            detail: {
              type: notificationType,
              message: toastMessage,
              variant: config.variant,
            },
          })
        );
      } else {
        Logger.debug(
          `🔥 MESSAGING: Skipping toast for ${notificationType} notification (skipToast: ${config.skipToast}, fromTap: ${fromTap})`
        );
      }
    }
  }

  /**
   * Initialize mobile messaging using Capacitor
   */
  async initializeMobile() {
    try {
      Logger.debug("🔥 MESSAGING: Starting mobile messaging initialization...");

      // Remove any existing listeners first to prevent duplicates
      await FirebaseMessaging.removeAllListeners();
      Logger.debug(
        "🔥 MESSAGING: Removed existing listeners to prevent duplicates"
      );

      // Get device information first
      this.deviceInfo = await Device.getInfo();
      this.deviceId = await Device.getId();

      // Request permission
      Logger.debug("🔥 MESSAGING: Requesting notification permissions...");
      const permission = await FirebaseMessaging.requestPermissions();
      Logger.debug("🔥 MESSAGING: Mobile notification permission result:");
      Logger.debug(JSON.stringify(permission));

      // Get FCM token
      Logger.debug("🔥 MESSAGING: Getting FCM token...");
      const tokenResult = await FirebaseMessaging.getToken();
      Logger.debug("🔥 MESSAGING: FCM token result:");
      Logger.debug(JSON.stringify(tokenResult));
      this.token = tokenResult.token;
      Logger.debug(
        "🔥 MESSAGING: Mobile FCM token obtained:",
        this.token ? "Success" : "Failed"
      );
      Logger.debug("🔥 MESSAGING: Token value:", this.token);
      Logger.debug("🔥 MESSAGING: Token stored in service:", !!this.token);
      Logger.debug(
        "🔥 MESSAGING: Token length:",
        this.token ? this.token.length : 0
      );

      // Store the token immediately to prevent race conditions
      if (this.token) {
        Logger.debug(
          "🔥 MESSAGING: Storing token immediately after getting it..."
        );
        await this.saveTokenToUser();
      }

      // Listen for token refresh
      Logger.debug("🔥 MESSAGING: Setting up token refresh listener...");
      FirebaseMessaging.addListener("tokenReceived", (event) => {
        Logger.debug("🔥 MESSAGING: Mobile FCM token refreshed:", event.value);
        if (event.value) {
          this.token = event.value;
          this.saveTokenToUser();
        } else {
          Logger.debug(
            "🔥 MESSAGING: Token refresh event received but value is undefined, ignoring"
          );
        }
      });

      // Listen for notifications (when app is in foreground)
      Logger.debug("🔥 MESSAGING: Setting up notification listener...");
      FirebaseMessaging.addListener("notificationReceived", (event) => {
        Logger.debug("🔥 MESSAGING: Mobile notification received:", event);
        this.handleMessage(event);
      });

      // Listen for notification tapped (when app is in background/closed)
      Logger.debug("🔥 MESSAGING: Setting up notification tap listener...");
      FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
        Logger.debug("🔥 MESSAGING: Notification tapped:", event);
        this.handleNotificationTap(event);
      });

      // Clear badge and delivered notifications on app open
      await this.clearBadge();

      // Clear badge whenever app resumes from background
      App.addListener("appStateChange", async (state) => {
        if (state.isActive) {
          await this.clearBadge();
        }
      });

      Logger.debug(
        "🔥 MESSAGING: Mobile messaging initialization completed successfully"
      );
      Logger.debug("🔥 MESSAGING: Final token state:", this.token);
    } catch (error) {
      Logger.error(
        "🔥 MESSAGING: Failed to initialize mobile messaging:",
        error
      );
      // Don't throw error to prevent app crash
    }
  }

  /**
   * Clear app badge count and delivered notifications
   */
  async clearBadge() {
    try {
      await FirebaseMessaging.removeAllDeliveredNotifications();
      Logger.debug("🔥 MESSAGING: Cleared badge and delivered notifications");
    } catch (error) {
      Logger.error("🔥 MESSAGING: Failed to clear badge:", error);
    }
  }

  /**
   * Reset messaging service state (for logout/login cycles)
   */
  async reset() {
    try {
      // Remove all listeners if on mobile
      if (this.isNativeMobile) {
        await FirebaseMessaging.removeAllListeners();
        Logger.debug("🔥 MESSAGING: Removed all listeners during reset");
      }
    } catch (error) {
      Logger.error(
        "🔥 MESSAGING: Error removing listeners during reset:",
        error
      );
    }

    this.isInitialized = false;
    this.token = null;
    this.messaging = null;
    this.deviceInfo = null;
    this.deviceId = null;
    Logger.debug("Messaging service state reset");
  }

  /**
   * Save FCM token to separate messaging collection
   */
  async saveTokenToUser() {
    if (
      !this.token ||
      !CurrentSession.isSignedIn ||
      !CurrentSession.authenticatedUser?.uid
    ) {
      Logger.debug(
        "🔥 MESSAGING: Cannot save token - missing token, not signed in, or no authenticated user"
      );
      return;
    }

    try {
      Logger.debug("🔥 MESSAGING: Saving FCM token to messaging collection...");

      // Create device-specific document name
      const deviceName = this.getDeviceDocumentName();
      const deviceModel = this.deviceInfo?.model || "unknown";

      // Save FCM token to messaging collection
      const messagingDocRef = doc(db, "messaging", this.documentId);

      // Check if document already exists
      const existingDoc = await getDoc(messagingDocRef);
      const isNewDocument = !existingDoc.exists();

      Logger.debug(
        `🔥 MESSAGING: Document ${
          isNewDocument ? "created" : "updated"
        } for device ${deviceModel} (ID: ${this.documentId})`
      );

      const now = new Date();
      const documentData = {
        uid: CurrentSession.authenticatedUser.uid,
        token: this.token,
        platform: this.isNativeMobile ? "mobile" : "web",
        deviceInfo: {
          model: this.deviceInfo?.model || "unknown",
          platform: this.deviceInfo?.platform || "unknown",
          operatingSystem: this.deviceInfo?.operatingSystem || "unknown",
          osVersion: this.deviceInfo?.osVersion || "unknown",
          manufacturer: this.deviceInfo?.manufacturer || "unknown",
          isVirtual: this.deviceInfo?.isVirtual || false,
          webViewVersion: this.deviceInfo?.webViewVersion || null,
        },
        deviceId: this.deviceId?.identifier || null,
        deviceName: deviceName,
        active: true,
        updated: now,
      };

      // Only set created field for new documents
      if (isNewDocument) {
        documentData.created = now;
        Logger.debug(
          `🔥 MESSAGING: Setting created field to ${now.toISOString()}`
        );
        // For new documents, we can use setDoc without merge
        await setDoc(messagingDocRef, documentData);
      } else {
        Logger.debug(
          `🔥 MESSAGING: Skipping created field for existing document`
        );
        // For existing documents, use merge to preserve the created field
        await setDoc(messagingDocRef, documentData, { merge: true });
      }

      Logger.debug(
        "🔥 MESSAGING: FCM token saved to messaging collection successfully"
      );
      Logger.debug(
        "🔥 MESSAGING: Token still exists after save:",
        !!this.token
      );
    } catch (error) {
      Logger.error(
        "🔥 MESSAGING: Failed to save FCM token to messaging collection:",
        error
      );
      // Don't throw error to prevent app crash
    }
  }

  /**
   * Show in-app notification for foreground messages
   */
  showInAppNotification(notification, data) {
    Logger.debug("🔥 MESSAGING: Showing in-app notification");
    // You can integrate with your app's notification system here
    // For example, show a toast, modal, or custom notification component

    // Example: Dispatch custom event that your app can listen to
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("fcmForegroundMessage", {
          detail: { notification, data },
        })
      );
    }
  }

  /**
   * Subscribe to a topic
   */
  async subscribeToTopic(topic) {
    try {
      if (this.isNativeMobile) {
        await FirebaseMessaging.subscribeToTopic({ topic });
        Logger.debug(`Subscribed to topic: ${topic}`);
      }
    } catch (error) {
      Logger.error(`Failed to subscribe to topic ${topic}:`, error);
    }
  }

  /**
   * Unsubscribe from a topic
   */
  async unsubscribeFromTopic(topic) {
    try {
      if (this.isNativeMobile) {
        await FirebaseMessaging.unsubscribeFromTopic({ topic });
        Logger.debug(`Unsubscribed from topic: ${topic}`);
      }
    } catch (error) {
      Logger.error(`Failed to unsubscribe from topic ${topic}:`, error);
    }
  }
}

// Create singleton instance
const messagingService = new MessagingService();
export default messagingService;
